'use client'
import { SetStateAction, useCallback, useEffect, useMemo, useReducer, useTransition } from 'react'
import {
  SET_AVAILABLE_DATES,
  SET_DATE,
  SET_HIDE_ROUND_TRIP_BUTTON,
  SET_INVENTORY,
  SET_LOADING,
  SET_NEXT_INVENTORY,
  SET_NEXT_SAIL_CONTENT,
  SET_PORTS,
  SET_REF_ID_AND_TIME,
  SET_SAIL_CONTENT,
} from '@/root/modules/shared/SelectDateTimePage/state/actions'
import {
  useAppConfig,
  useCustomRouter,
  useErrorBanner,
  useGetAvailableDates,
  useGetSailPackage,
} from '@/root/shared-hooks'
import {
  getInitialTimestamp,
  getModifiedInventory,
  getTravelDuration,
  getVessel,
  setEarliestTimeAndRefId,
  reverseTitleParts,
} from '@/root/modules/shared/SelectDateTimePage'
import { Direction, Inventory, Locale, SailContent, SailPackage } from '@/root/shared-types'
import { SailParam } from '@/root/modules/shared/SelectDateTimePage/helpers/parseSailPackages'
import { selectDateTimeReducer } from '@/root/modules/shared/SelectDateTimePage/state/reducer'
import { SelectDateTimeState } from '@/root/modules/shared/SelectDateTimePage/state/types'
import { defaultInventory, regexPatterns } from '@/root/shared-consts'
import { DateTime } from 'luxon'
import * as R from 'ramda'
import { useParams } from 'next/navigation'

export interface UseSailSelectParams {
  sailParams: SailParam[]
  inventory: Inventory[]
  sailContent: SailContent[][]
  routeBaseInfo: SailPackage
}

type UpdateParamRecord = Partial<Record<'dates' | 'packages', string | string[]>>

const convertToDateOnly = (date: Date) => String(DateTime.fromJSDate(date).toISODate())

const { separatorDash } = regexPatterns

export const useSailSelect = ({ routeBaseInfo, sailParams, inventory, sailContent }: UseSailSelectParams) => {
  // Slots 3–4 hold next-day sails (night sails after midnight shown with the selected date).
  // No live client passes them yet: the route sends two slots, and the hook falls back to empty data.
  const [inventoryThere, inventoryBack, nextInventoryThere, nextInventoryBack] = inventory
  const [sailContentThere, sailContentBack, nextSailContentThere, nextSailContentBack] = sailContent
  const fallbackPackageId = routeBaseInfo.backwardSailPackage ?? ''

  const { locale, replace } = useCustomRouter()

  const params = useParams<{
    locale: Locale
    packages: string
    dates: string
  }>()

  const [, startTransition] = useTransition()

  const { setSailPackage } = useGetSailPackage()
  const { defaultTimezone } = useAppConfig()
  const { errorBody } = useErrorBanner()

  const updateUrlOnly = useCallback(
    async (updateRecord: UpdateParamRecord) => {
      if (!params) return

      const newPackages =
        updateRecord.packages !== undefined
          ? Array.isArray(updateRecord.packages)
            ? updateRecord.packages.join(',')
            : updateRecord.packages
          : params.packages

      const newDates =
        updateRecord.dates !== undefined
          ? Array.isArray(updateRecord.dates)
            ? updateRecord.dates.join(',')
            : updateRecord.dates
          : params.dates

      replace(`/${params.locale}/select-date-sail/${newPackages}/${newDates}`, { scroll: false })
    },
    [params, replace]
  )

  const [packageIdThere, packageIdBack] = [sailParams[0]?.code, sailParams[1]?.code || fallbackPackageId]
  const [dateTo, dateFrom] = [sailParams[0]?.sailDates[0], sailParams[1]?.sailDates[0]]

  const availableDatesTo = useGetAvailableDates({ sailPackage: packageIdThere })
  const availableDatesFrom = useGetAvailableDates({ sailPackage: packageIdBack })

  const today = new Date()
  const toDate = dateTo ? new Date(String(dateTo)) : today
  const fromDate = dateFrom ? new Date(String(dateFrom)) : toDate

  const initialState: SelectDateTimeState = {
    sail: {
      there: {
        date: toDate,
        availableDates: availableDatesTo,
        port: '',
        inventory: defaultInventory,
        nextInventory: defaultInventory,
        sailContent: [],
        nextSailContent: [],
        loading: true,
        sailRefId: -1,
        time: '',
        isNextDay: false,
      },
      back: {
        date: fromDate,
        availableDates: availableDatesFrom,
        port: '',
        inventory: defaultInventory,
        nextInventory: defaultInventory,
        sailContent: [],
        nextSailContent: [],
        loading: true,
        sailRefId: -1,
        time: '',
        isNextDay: false,
      },
    },
    hideRoundTripButton: Boolean(dateFrom),
  }

  const [state, dispatch] = useReducer(selectDateTimeReducer, initialState)
  const { there, back } = state.sail

  // Sync inventory data from props to state
  useEffect(() => {
    dispatch({ type: SET_INVENTORY, payload: { direction: 'to', inventory: inventoryThere ?? defaultInventory } })
    dispatch({
      type: SET_NEXT_INVENTORY,
      payload: { direction: 'to', nextInventory: nextInventoryThere ?? defaultInventory },
    })
    dispatch({ type: SET_INVENTORY, payload: { direction: 'from', inventory: inventoryBack ?? defaultInventory } })
    dispatch({
      type: SET_NEXT_INVENTORY,
      payload: { direction: 'from', nextInventory: nextInventoryBack ?? defaultInventory },
    })
  }, [inventoryThere, nextInventoryThere, inventoryBack, nextInventoryBack])

  // Sync sail content from props to state
  useEffect(() => {
    dispatch({ type: SET_SAIL_CONTENT, payload: { direction: 'to', sailContent: sailContentThere ?? [] } })
    dispatch({ type: SET_NEXT_SAIL_CONTENT, payload: { direction: 'to', nextSailContent: nextSailContentThere ?? [] } })
    dispatch({ type: SET_SAIL_CONTENT, payload: { direction: 'from', sailContent: sailContentBack ?? [] } })
    dispatch({
      type: SET_NEXT_SAIL_CONTENT,
      payload: { direction: 'from', nextSailContent: nextSailContentBack ?? [] },
    })
  }, [sailContentThere, nextSailContentThere, sailContentBack, nextSailContentBack])

  useEffect(() => {
    if (routeBaseInfo?.titles) {
      const routeName = routeBaseInfo.titles[locale] || ''
      const [from = '', to = ''] = routeName.split(separatorDash).map((part: string) => part.trim())

      dispatch({ type: SET_PORTS, payload: { from, to } })
    }
  }, [routeBaseInfo?.titles, locale])

  useEffect(() => {
    if (availableDatesTo.length > 0 && there.availableDates.length === 0) {
      dispatch({ type: SET_AVAILABLE_DATES, payload: { direction: 'to', availableDates: availableDatesTo } })
    }
    if (availableDatesFrom.length > 0 && back.availableDates.length === 0) {
      dispatch({ type: SET_AVAILABLE_DATES, payload: { direction: 'from', availableDates: availableDatesFrom } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableDatesTo.length, availableDatesFrom.length, packageIdThere, packageIdBack])

  const modInvThere = useMemo(
    () =>
      getModifiedInventory({
        inventory: there.inventory,
        nextInventory: there.nextInventory,
        sailContent: there.sailContent,
        nextSailContent: there.nextSailContent,
      }),
    [there.inventory, there.nextInventory, there.nextSailContent, there.sailContent]
  )

  const modInvBack = useMemo(
    () =>
      getModifiedInventory({
        inventory: back.inventory,
        nextInventory: back.nextInventory,
        sailContent: back.sailContent,
        nextSailContent: back.nextSailContent,
      }),
    [back.inventory, back.nextInventory, back.nextSailContent, back.sailContent]
  )

  useEffect(() => {
    setEarliestTimeAndRefId({
      defaultTimezone,
      direction: 'to',
      inventory: modInvThere,
      dispatch,
    })
  }, [defaultTimezone, modInvThere])

  const startTimestampForBack = useMemo(
    () =>
      getInitialTimestamp({
        startDay: there.date,
        startTime: there.time,
        isNextDay: there.isNextDay,
      }),
    [there.date, there.isNextDay, there.time]
  )

  useEffect(() => {
    setEarliestTimeAndRefId({
      defaultTimezone,
      direction: 'from',
      inventory: modInvBack,
      dispatch,
      initialTime: startTimestampForBack,
    })
  }, [defaultTimezone, modInvBack, startTimestampForBack])

  const handleDateChange = (direction: Direction) => async (date: Date) => {
    if (direction === 'to') {
      await updateUrlOnly({
        dates: state.hideRoundTripButton
          ? [
              convertToDateOnly(date),
              convertToDateOnly(date > state.sail.back.date ? date : state.sail.back.date),
            ].join(',')
          : convertToDateOnly(date),
      })
    } else {
      await updateUrlOnly({ dates: [convertToDateOnly(state.sail.there.date), convertToDateOnly(date)].join(',') })
    }
    dispatch({ type: SET_DATE, payload: { direction, date } })
  }

  const handleRefIdAndTimeChange = (direction: Direction) => (sailRefId: number, time: string, isNextDay: boolean) => {
    dispatch({ type: SET_REF_ID_AND_TIME, payload: { direction, refIdAndTime: { sailRefId, time, isNextDay } } })
  }

  const setHideRoundTripButton = (hide: SetStateAction<boolean>) => {
    dispatch({ type: SET_HIDE_ROUND_TRIP_BUTTON, payload: hide })
    if (hide) {
      startTransition(() => {
        void updateUrlOnly({
          packages: [packageIdThere, packageIdBack].filter(Boolean).join(','),
          dates: [convertToDateOnly(state.sail.there.date), convertToDateOnly(state.sail.back?.date)].join(','),
        })
      })
    } else {
      startTransition(() => {
        void updateUrlOnly({
          packages: packageIdThere,
          dates: convertToDateOnly(state.sail.there.date),
        })
      })
    }
  }

  const vesselThere = getVessel({ inventory: modInvThere, sailRefId: there.sailRefId })
  const vesselBack = getVessel({ inventory: modInvBack, sailRefId: back.sailRefId })

  const packages = state.hideRoundTripButton ? [packageIdThere, packageIdBack] : [packageIdThere]
  const sailRefIds = state.hideRoundTripButton ? [there.sailRefId, back.sailRefId] : [there.sailRefId]

  const isDifferentShipsForRoute = state.hideRoundTripButton && !R.equals(vesselThere, vesselBack)
  const isDisabled = Boolean(
    R.equals(there.sailRefId, -1) ||
    isDifferentShipsForRoute ||
    errorBody ||
    there.loading ||
    (state.hideRoundTripButton && back.loading)
  )

  const handleSwapPorts = async () => {
    dispatch({ type: SET_LOADING, payload: { direction: 'from', loading: true } })
    dispatch({ type: SET_LOADING, payload: { direction: 'to', loading: true } })

    setSailPackage({
      code: packageIdBack,
      backwardSailPackage: packageIdThere,
      roundtrip: routeBaseInfo?.roundtrip ?? false,
      sailRefs: [],
      route: routeBaseInfo?.route || '',
      title: (routeBaseInfo?.titles && reverseTitleParts(routeBaseInfo.titles).en) ?? '',
      titles: routeBaseInfo?.titles && reverseTitleParts(routeBaseInfo.titles),
      routeAttributes: routeBaseInfo?.routeAttributes,
    })
    const newFirstPackage = packageIdBack
    const newSecondPackage = packageIdThere
    const packagesParamList = state.hideRoundTripButton ? [newFirstPackage, newSecondPackage] : [newFirstPackage]
    await updateUrlOnly({ packages: packagesParamList.join(',') })
  }

  const travelThere = useMemo(
    () =>
      getTravelDuration({
        voyages: there.sailContent,
        nextVoyages: there.nextSailContent,
        sailRefId: there.sailRefId,
      }),
    [there.nextSailContent, there.sailContent, there.sailRefId]
  )

  const travelBack = useMemo(
    () =>
      getTravelDuration({
        voyages: back.sailContent,
        nextVoyages: back.nextSailContent,
        sailRefId: back.sailRefId,
      }),
    [back.nextSailContent, back.sailContent, back.sailRefId]
  )

  return {
    locale,
    there,
    back,
    hideRoundTripButton: state.hideRoundTripButton,
    packageIdBack,
    modInvThere,
    modInvBack,
    travelThere,
    travelBack,
    startTimestampForBack,
    handleDateChange,
    handleRefIdAndTimeChange,
    setHideRoundTripButton,
    handleSwapPorts,
    packages,
    sailRefIds,
    isDisabled,
  }
}
